import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguageContext } from "@/contexts/LanguageContext";
import { deepLService } from "@/services/deepLService";
import { SOURCE_LANGUAGE } from "@/config/languages";

/**
 * Translates the French interface text of public Sokoby pages and storefronts
 * into the selected language. Merchant content is marked translate="no" and skipped.
 * Signed-in dashboard pages are not touched.
 */
const PUBLIC_PATHS = new Set([
  "/", "/tarifs", "/pricing", "/plan-tarifaire", "/fonctionnalites", "/features", "/ressources", "/resources",
  "/contact", "/about", "/a-propos", "/qui-sommes-nous", "/essai-gratuit", "/legal", "/accessibility",
  "/conditions", "/support", "/marketplace", "/extensions", "/connexion", "/login", "/inscription",
  "/register", "/mot-de-passe-oublie", "/forgot-password", "/boutique", "/boutique-ia", "/roadmap",
  "/creer-boutique-ia", "/guides", "/blog", "/help-center", "/centre-aide", "/comparaison-modeles",
]);
const isPublic = (p: string) =>
  PUBLIC_PATHS.has(p.replace(/\/+$/, "") || "/") || /^\/(boutique-apercu|product|products)\//.test(p);

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA", "INPUT", "CODE", "PRE", "SVG"]);
const HAS_LETTERS = /[A-Za-zÀ-ÿ]{2,}/;

type Entry = { original: string; shown: string };
const textState = new WeakMap<Text, Entry>();
const phState = new WeakMap<Element, Entry>();

function excluded(el: Element | null): boolean {
  for (let e = el; e; e = e.parentElement) {
    if (SKIP_TAGS.has(e.tagName.toUpperCase())) return true;
    if (e.getAttribute("translate") === "no" || e.hasAttribute("data-i18n-done") || (e as HTMLElement).isContentEditable) return true;
  }
  return false;
}

export function AutoTranslate() {
  const { currentLanguage, isTranslationEnabled } = useLanguageContext();
  const { pathname } = useLocation();

  useEffect(() => {
    const lang = isTranslationEnabled && isPublic(pathname) ? currentLanguage : SOURCE_LANGUAGE;
    let active = true;

    const handleText = (node: Text) => {
      let st = textState.get(node);
      if (!st || node.data !== st.shown) {
        st = { original: node.data, shown: node.data };
        textState.set(node, st);
      }
      const src = st.original;
      if (lang === SOURCE_LANGUAGE) {
        if (node.data !== src) { st.shown = src; node.data = src; }
        return;
      }
      const core = src.trim();
      if (!core || core.length > 3000 || !HAS_LETTERS.test(core) || excluded(node.parentElement)) return;
      const apply = (tr: string) => {
        if (!active || textState.get(node) !== st || node.data !== st!.shown) return;
        const out = src.replace(core, tr);
        st!.shown = out; if (node.data !== out) node.data = out;
      };
      const cached = deepLService.getCached(core, lang);
      if (cached) apply(cached); else deepLService.translate(core, lang).then(apply);
    };

    const handlePlaceholder = (el: Element) => {
      const cur = el.getAttribute("placeholder") || "";
      let st = phState.get(el);
      if (!st || cur !== st.shown) { st = { original: cur, shown: cur }; phState.set(el, st); }
      const src = st.original;
      if (lang === SOURCE_LANGUAGE) { if (cur !== src) { st.shown = src; el.setAttribute("placeholder", src); } return; }
      if (!HAS_LETTERS.test(src) || (el.parentElement && excluded(el.parentElement)) || el.getAttribute("translate") === "no") return;
      const s = st;
      deepLService.translate(src.trim(), lang).then((tr) => {
        if (!active || phState.get(el) !== s || el.getAttribute("placeholder") !== s.shown) return;
        s.shown = tr; if (el.getAttribute("placeholder") !== tr) el.setAttribute("placeholder", tr);
      });
    };

    const scan = (root: Node) => {
      if (root.nodeType === Node.TEXT_NODE) return handleText(root as Text);
      if (root.nodeType !== Node.ELEMENT_NODE) return;
      const el = root as Element;
      if (el.hasAttribute("placeholder")) handlePlaceholder(el);
      el.querySelectorAll("[placeholder]").forEach(handlePlaceholder);
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      for (let n = walker.nextNode(); n; n = walker.nextNode()) handleText(n as Text);
    };

    scan(document.body);
    const obs = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type === "characterData") handleText(m.target as Text);
        else if (m.type === "attributes") handlePlaceholder(m.target as Element);
        else m.addedNodes.forEach(scan);
      }
    });
    obs.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ["placeholder"] });
    // Re-check the page shortly after switching so any text whose translation failed or arrived late is filled in.
    const retries = lang === SOURCE_LANGUAGE ? [] : [2500, 6000, 12000].map((ms) => setTimeout(() => active && scan(document.body), ms));
    return () => { active = false; obs.disconnect(); retries.forEach(clearTimeout); };
  }, [currentLanguage, isTranslationEnabled, pathname]);

  return null;
}
